import { useState } from 'react';
import { Link } from 'react-router';
import { api, type SupportRequestView } from '../../api/client';
import { Button, Card, Chip, Empty, LoadError, Loading, PageHeader } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const TABS = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Done' },
  { id: 'all', label: 'All' }
] as const;

/** Requests for help from the help panel, for Stitch staff to pick up. */
export function AdminSupport() {
  const [tab, setTab] = useState<(typeof TABS)[number]['id']>('open');
  const list = useData(() => api.supportRequests(tab), [tab]);
  const [busy, setBusy] = useState<string | null>(null);

  async function setStatus(r: SupportRequestView, status: 'open' | 'closed') {
    setBusy(r.id);
    try {
      await api.setSupportStatus(r.id, status);
      list.reload();
    } finally {
      setBusy(null);
    }
  }

  const requests = list.data?.requests ?? [];
  return (
    <>
      <PageHeader eyebrow="Platform" title="Support" chips={list.data?.open ? <Chip tone="crit">{list.data.open} open</Chip> : undefined} />
      <div className="content">
        <div className="segmented" role="group" aria-label="Show">
          {TABS.map((t) => (
            <button key={t.id} type="button" aria-pressed={tab === t.id} className={tab === t.id ? 'on' : undefined} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        {list.error ? (
          <Card>
            <LoadError error={list.error} retry={list.reload} />
          </Card>
        ) : !list.data ? (
          <Card>
            <Loading label="Loading requests" />
          </Card>
        ) : requests.length === 0 ? (
          <Card>
            <Empty title={tab === 'open' ? 'Nothing waiting' : 'No requests'}>Requests sent from the help panel in the portal show here, and are emailed to SUPPORT_EMAIL when email is set up.</Empty>
          </Card>
        ) : (
          <ul className="support-list">
            {requests.map((r) => (
              <li key={r.id} className="card support-item">
                <div className="support-item-head">
                  <span className="cell-stack">
                    <strong>{r.userName || r.userEmail}</strong>
                    <span className="muted">
                      {r.userEmail}
                      {r.company && (
                        <>
                          {' · '}
                          {r.tenant ? <Link to={`/t/${encodeURIComponent(r.tenant)}`}>{r.company}</Link> : r.company}
                        </>
                      )}
                    </span>
                  </span>
                  <span className="support-when">
                    {r.online && <Chip tone="ok">Online now</Chip>}
                    {r.status === 'closed' ? <Chip tone="ok">Done</Chip> : <Chip tone="warn">Open</Chip>}
                    <span className="muted">{shortWhen(r.createdAt)}</span>
                  </span>
                </div>
                <p className="support-message">{r.message}</p>
                <div className="support-item-foot">
                  {r.page && (
                    <span className="muted support-page">
                      <Icon name="link" size={14} /> From {r.page.startsWith('/t/') || r.page.startsWith('/admin') ? <Link to={r.page}>{r.page}</Link> : r.page}
                    </span>
                  )}
                  <span className="support-buttons">
                    {r.online && r.userId && r.status === 'open' && (
                      <a className="btn primary small" href={`/live/new?user=${encodeURIComponent(r.userId)}`} target="_blank" rel="noopener">
                        <Icon name="screen" size={15} /> Join their screen
                      </a>
                    )}
                    <a className="btn secondary small" href={`mailto:${r.userEmail}?subject=${encodeURIComponent('Your Stitch help request')}`}>
                      <Icon name="mail" size={15} /> Reply by email
                    </a>
                    {r.status === 'open' ? (
                      <Button size="small" icon="check" disabled={busy === r.id} onClick={() => void setStatus(r, 'closed')}>
                        Mark done
                      </Button>
                    ) : (
                      <Button size="small" variant="ghost" disabled={busy === r.id} onClick={() => void setStatus(r, 'open')}>
                        Reopen
                      </Button>
                    )}
                  </span>
                </div>
                {r.closedBy && r.status === 'closed' && <span className="muted small-print">Done by {r.closedBy}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
